import React from 'react'

import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'

import { Text } from '@mxenabled/mxui'

import { useScreenSize } from '../../common'
import { AnalysisType } from '../../core/constants/Investments'
import { formatKey } from '../../core/utils/InvestmentUtil'

import { EmptyStateFooterComponent } from './EmptyStateFooterComponent'
import { formatCurrency } from '../../core/utils/NumberFormatting'

interface Copy {
  bonds: string
  equities: string
  empty_data: string
}

interface AnalysisDetailsProps {
  analysisDetails: Record<string, number>
  analysisType: AnalysisType
  copy: Copy
  setAnalysisType: (type: AnalysisType) => void
}

const AnalysisDetails: React.FC<AnalysisDetailsProps> = ({
  analysisDetails,
  analysisType,
  copy,
  setAnalysisType,
}) => {
  const { isSmallMobile } = useScreenSize()

  const listItems = React.useMemo(() => {
    return Object.keys(analysisDetails)
      .filter((key) => analysisDetails[key] >= 1)
      .map((key) => ({
        key,
        value: formatCurrency(analysisDetails[key], '0,0'),
        formattedKey: formatKey(key),
      }))
  }, [analysisDetails])

  const toggleButtons = [
    { label: copy.equities, value: AnalysisType.Stocks },
    { label: copy.bonds, value: AnalysisType.Bonds },
  ]
  return (
    <Stack sx={{ gap: 12, width: { md: 412 } }}>
      <ToggleButtonGroup
        color="primary"
        exclusive={true}
        onChange={(_, value) => value && setAnalysisType(value)}
        sx={{
          width: '100%',
          '& .MuiToggleButton-root': {
            color: 'secondary.main',
            fontWeight: 600,
            textTransform: 'capitalize',
          },
        }}
        value={listItems.length ? analysisType : -1}
      >
        {toggleButtons.map(({ label, value }) => (
          <ToggleButton key={value} sx={{ width: '100%' }} value={value}>
            {label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {listItems.length ? (
        <List
          sx={{
            '& .MuiListItemSecondaryAction-root': { pr: 12 },
            '& .MuiListItem-divider': { borderColor: 'neutral.light' },
            '& .MuiListItem-root': { minHeight: 44, px: 12 },
          }}
        >
          {listItems.map(({ value, formattedKey }) => (
            <ListItem divider={true} key={formattedKey} secondaryAction={<Text>{value}</Text>}>
              <ListItemText
                primary={formattedKey}
                slotProps={{ primary: { fontStyle: 'normal' } }}
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <>
          <Text sx={{ color: 'secondary.main', p: 8, textAlign: 'center' }}>{copy.empty_data}</Text>
          <Divider sx={{ borderColor: 'neutral.light' }} />
          <EmptyStateFooterComponent
            sx={{
              '.inner-container': { width: isSmallMobile ? '100%' : 364 },
            }}
          />
        </>
      )}
    </Stack>
  )
}

export default AnalysisDetails
